import { useId, useState } from 'react';
import { Button } from '@heroui/react';
import { Sheet } from '@heroui-pro/react/sheet';
import {
  ArrowRight,
  CalendarDays,
  Check,
  ChevronDown,
  CircleCheck,
  Clock3,
  Layers2,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  TriangleAlert,
  UsersRound,
  Zap,
} from 'lucide-react';
import {
  BLOCKS,
  MANUAL_RULE,
  MAX_SHEETS_PER_TANDEM,
  scoreOf,
  type RuleDef,
  type ScoringConfig,
} from '@odd/domain';
import './RulesSheet.css';

const number = (value: number) => value.toLocaleString('es-ES', { maximumFractionDigits: 2 });
function humanValue(rule: RuleDef, value: number) {
  const n = number(value);
  switch (rule.unit) {
    case 'd':
      return `${n} ${value === 1 ? 'día' : 'días'}`;
    case 'dl':
      return `${n} ${value === 1 ? 'día laborable' : 'días laborables'}`;
    case 'm':
      return `${n} ${value === 1 ? 'mes' : 'meses'}`;
    case 're':
      return `${n} ${value === 1 ? 'reapertura' : 'reaperturas'}`;
    case 'R':
      return `nivel ${n}`;
    case '%':
      return `${n} %`;
  }
}
const thresholdText = (rule: RuleDef, value: number) =>
  `${humanValue(rule, value)} o ${rule.inv ? 'menos' : 'más'}`;

function SelectionGraphic({ scoring: s }: { scoring: ScoringConfig }) {
  return (
    <ol className="rules-process" aria-label="Cómo se prepara la mesa">
      <li className="rules-process-step">
        <div className="rules-step-label">
          <span>1</span> Detectar
        </div>
        <div className="rules-process-graphic rules-signals" aria-hidden="true">
          <span className="rules-signal rules-tone--ok">
            <CircleCheck size={15} /> En plazo <small>Se cuenta</small>
          </span>
          <span className="rules-signal rules-tone--warn">
            <Clock3 size={15} /> Ámbar <ArrowRight size={14} />
          </span>
          <span className="rules-signal rules-tone--bad">
            <TriangleAlert size={15} /> Rojo <ArrowRight size={14} />
          </span>
        </div>
        <h3>Lo que necesita atención</h3>
        <p>Las alertas ámbar y rojas pasan a la selección. Lo que está en plazo se queda en su contador.</p>
      </li>
      <li className="rules-process-step">
        <ArrowRight className="rules-process-arrow" aria-hidden="true" />
        <div className="rules-step-label">
          <span>2</span> Priorizar
        </div>
        <div className="rules-process-graphic rules-order">
          {s.table.ordering === 'tiered' ? (
            <>
              <div className="rules-segment-order">
                <strong>A + B</strong>
                <ArrowRight size={15} aria-hidden="true" />
                <span>C · D · E</span>
              </div>
              <div className="rules-light-order">
                <span className="rules-tone--bad">Rojo</span>
                <ArrowRight size={12} aria-hidden="true" />
                <span className="rules-tone--warn">Ámbar</span>
              </div>
              <small>Dentro de cada grupo</small>
            </>
          ) : (
            <div className="rules-score-order">
              <SlidersHorizontal size={25} aria-hidden="true" />
              <strong>Más puntos, antes</strong>
              <small>Se aplica a todos los segmentos</small>
            </div>
          )}
        </div>
        <h3>
          {s.table.ordering === 'tiered' ? 'Primero las comunidades A y B' : 'Primero la mayor puntuación'}
        </h3>
        <p>
          {s.table.ordering === 'tiered'
            ? 'Después, el resto. En cada grupo, rojo antes que ámbar; los puntos desempatan.'
            : 'Se suman el estado, el segmento, la junta próxima y la intensidad del problema, con los ajustes de cada alerta.'}
        </p>
      </li>
      <li className="rules-process-step">
        <ArrowRight className="rules-process-arrow" aria-hidden="true" />
        <div className="rules-step-label">
          <span>3</span> Preparar la reunión
        </div>
        <div className="rules-process-graphic rules-table-graphic" aria-hidden="true">
          <div className="rules-table-cap">
            <strong>{s.table.max}</strong>
            <span>casos como máximo</span>
          </div>
          <div className="rules-table-slots">
            {Array.from({ length: s.table.max }, (_, i) => (
              <i key={i} />
            ))}
          </div>
        </div>
        <h3>Una mesa para poder avanzar</h3>
        <p>Se eligen hasta {s.table.max} casos, respetando el reparto entre bloques que ves más abajo.</p>
      </li>
    </ol>
  );
}

function PointsDetails({
  rules,
  rule,
  scoring: s,
}: {
  rules: RuleDef[];
  rule?: RuleDef;
  scoring: ScoringConfig;
}) {
  const sampleScore =
    rule && rule.red > 0
      ? scoreOf(
          { ...rule, ruleId: rule.id, value: rule.red, segment: 'A', daysToMeeting: s.meetingSoon.days },
          s,
        )
      : null;
  const exampleRatio = rule?.inv ? rule.red / Math.max(rule.red, 0.1) : 1;
  const exampleParts = [
    { label: 'el color', points: s.lightPoints.rojo },
    { label: 'el segmento', points: s.segmentPoints.A },
    { label: 'la junta', points: s.meetingSoon.points },
    { label: 'la intensidad', points: Math.min(exampleRatio, s.ratio.cap) * s.ratio.weight },
    ...(rule && s.ruleBonus[rule.id] ? [{ label: 'el ajuste', points: s.ruleBonus[rule.id] }] : []),
  ];
  const exampleSum = exampleParts
    .map(
      (part, index) =>
        `${index > 0 ? (part.points < 0 ? ' − ' : ' + ') : part.points < 0 ? '−' : ''}${number(Math.abs(part.points))} por ${part.label}`,
    )
    .join('');
  const bonuses = rules.filter((item) => (s.ruleBonus[item.id] ?? 0) !== 0);
  return (
    <div className="rules-disclosure-content">
      <p>
        Los puntos ayudan a comparar casos.{' '}
        {s.table.ordering === 'tiered'
          ? 'En la configuración actual, solo deciden después del segmento y del color.'
          : 'En la configuración actual, son el criterio principal de orden.'}
      </p>
      <dl className="rules-point-factors">
        <div>
          <dt>Color del caso</dt>
          <dd>
            <span className="rules-tone--bad">Rojo: {number(s.lightPoints.rojo)}</span>
            <span className="rules-tone--warn">Ámbar: {number(s.lightPoints.ambar)}</span>
          </dd>
        </div>
        <div>
          <dt>Segmento de la comunidad</dt>
          <dd>
            {(['A', 'B', 'C', 'D', 'E', 'none'] as const).map((segment) => (
              <span key={segment}>
                {segment === 'none' ? 'Sin segmento' : segment}: {number(s.segmentPoints[segment])}
              </span>
            ))}
          </dd>
        </div>
        <div>
          <dt>Junta próxima</dt>
          <dd>
            {number(s.meetingSoon.points)} puntos si hay junta{' '}
            {s.meetingSoon.days === 0 ? 'hoy' : `en ${s.meetingSoon.days} días o menos`}.
          </dd>
        </div>
        <div>
          <dt>Cuánto empeora el dato</dt>
          <dd>
            {s.ratio.weight < 0 ? 'Resta' : 'Añade'} hasta {number(Math.abs(s.ratio.weight * s.ratio.cap))}{' '}
            puntos, comparando el valor del caso con el límite rojo.
          </dd>
        </div>
        {bonuses.length > 0 && (
          <div>
            <dt>Ajustes por tipo de alerta</dt>
            <dd>
              {bonuses.map((item) => (
                <span key={item.id}>
                  {item.name}: {s.ruleBonus[item.id] > 0 ? '+' : ''}
                  {number(s.ruleBonus[item.id])}
                </span>
              ))}
            </dd>
          </div>
        )}
      </dl>
      {rule && sampleScore != null && Number.isFinite(sampleScore) && (
        <div className="rules-score-example">
          <div>
            <small>Ejemplo con esta configuración</small>
            <h4>{rule.name}</h4>
            <p>
              {humanValue(rule, rule.red)}, comunidad A y junta{' '}
              {s.meetingSoon.days === 0 ? 'hoy' : `en ${s.meetingSoon.days} días`}.
            </p>
            <div className="rules-score-breakdown" role="group" aria-label="Suma del ejemplo">
              {exampleSum}
            </div>
          </div>
          <div className="rules-score-total">
            <strong>{number(sampleScore)}</strong>
            <span>puntos</span>
          </div>
        </div>
      )}
      <p className="rules-footnote">
        La intensidad se calcula con el valor dividido por el límite rojo; en los indicadores inversos, al
        revés. Se limita a {number(s.ratio.cap)} y se multiplica por {number(s.ratio.weight)}. El total se
        redondea. Los casos manuales no suman este factor.
      </p>
    </div>
  );
}

/** Guía visual con la configuración real; los detalles de cálculo se consultan a demanda. */
export function RulesSheet({
  isOpen,
  onClose,
  rules,
  scoring: s,
  onOpenAdmin,
}: {
  isOpen: boolean;
  onClose: () => void;
  rules: RuleDef[];
  scoring: ScoringConfig;
  onOpenAdmin?: () => void;
}) {
  const id = useId();
  const [exampleId, setExampleId] = useState('J1');
  const systemRules = rules.filter((rule) => rule.id !== MANUAL_RULE);
  const enabledRules = systemRules.filter((rule) => rule.enabled);
  const examples = enabledRules.length ? enabledRules : systemRules;
  const example = examples.find((rule) => rule.id === exampleId) ?? examples[0];
  const totalMinutes = BLOCKS.reduce((sum, block) => sum + s.blockMinutes[block.id], 0);
  const allocationText =
    s.table.allocation === 'global'
      ? 'Se eligen los primeros casos por orden de prioridad, sin reservar plazas por bloque.'
      : s.table.allocation === 'minimum'
        ? `Se intenta reservar hasta ${s.table.minPerBlock} plazas por bloque con alertas. Los huecos restantes se asignan por prioridad, hasta completar el máximo de ${s.table.max}.`
        : totalMinutes > 0
          ? 'Los bloques con alertas reciben plazas en proporción a sus minutos. Si alguno no las necesita, sus huecos se aprovechan para otros casos.'
          : `No hay minutos asignados a los bloques. La selección sigue respetando el límite de ${s.table.max} casos y el orden de prioridad.`;

  return (
    <Sheet isOpen={isOpen} onOpenChange={(open) => !open && onClose()} placement="right">
      <Sheet.Backdrop>
        <Sheet.Content className="rules-sheet">
          <Sheet.Dialog className="rules-dialog">
            <Sheet.CloseTrigger aria-label="Cerrar ayuda" />
            <Sheet.Header className="rules-header">
              <span className="rules-eyebrow">Entender tu reunión</span>
              <Sheet.Heading className="rules-heading">Cómo se calcula</Sheet.Heading>
              <p>De todas las alertas de tu oficina a los casos que conviene tratar esta semana.</p>
            </Sheet.Header>
            <Sheet.Body className="rules-body" data-sheet-no-drag>
              <section aria-labelledby={`${id}-overview`}>
                <div className="rules-section-intro">
                  <h3 id={`${id}-overview`}>Tu mesa, en tres pasos</h3>
                  <small>Con la configuración actual</small>
                </div>
                <SelectionGraphic scoring={s} />
                <div className="rules-search-note">
                  <Search size={17} aria-hidden="true" />
                  <p>
                    <strong>Los que no entran siguen disponibles.</strong> Búscalos desde la mesa. El filtro
                    A+B/Todos y tus permisos determinan qué alertas puedes consultar.
                  </p>
                </div>
              </section>
              {example && (
                <section aria-labelledby={`${id}-colors`}>
                  <div className="rules-section-intro rules-colors-intro">
                    <div>
                      <span className="rules-eyebrow">El semáforo</span>
                      <h3 id={`${id}-colors`}>Qué significa cada color</h3>
                    </div>
                    <div className="rules-example-picker">
                      <label htmlFor={`${id}-example`}>Ver ejemplo de</label>
                      <div>
                        <select
                          id={`${id}-example`}
                          value={example.id}
                          onChange={(event) => setExampleId(event.target.value)}
                        >
                          {examples.map((rule) => (
                            <option key={rule.id} value={rule.id}>
                              {rule.name}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={14} aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                  <p className="rules-example-measure">
                    {example.measure}
                    {example.inv && <span> · Cuanto más bajo, peor</span>}
                  </p>
                  {!example.enabled && (
                    <p className="rules-example-unavailable">
                      Este tipo de alerta todavía no tiene datos disponibles.
                    </p>
                  )}
                  <div
                    className="rules-threshold-graphic"
                    role="group"
                    aria-label={`Semáforo de ${example.name}`}
                  >
                    <div className="rules-threshold rules-tone--ok">
                      <span>
                        <CircleCheck size={17} aria-hidden="true" /> En plazo
                      </span>
                      <strong>
                        {example.inv ? 'Más' : 'Menos'} de {humanValue(example, example.amber)}
                      </strong>
                      <p>Se cuenta, pero no ocupa un hueco en la mesa.</p>
                    </div>
                    <div className="rules-threshold rules-tone--warn">
                      <span>
                        <Clock3 size={17} aria-hidden="true" /> Revisar
                      </span>
                      <strong>{thresholdText(example, example.amber)}</strong>
                      <p>Si aún no ha llegado al límite rojo.</p>
                    </div>
                    <div className="rules-threshold rules-tone--bad">
                      <span>
                        <TriangleAlert size={17} aria-hidden="true" /> Atender
                      </span>
                      <strong>{thresholdText(example, example.red)}</strong>
                      <p>Ha alcanzado el nivel de mayor atención.</p>
                    </div>
                  </div>
                  <p className="rules-footnote">
                    Cada tipo de alerta tiene sus propios límites. En los casos añadidos a mano, el color lo
                    elige quien los registra.
                  </p>
                </section>
              )}
              <section aria-labelledby={`${id}-allocation`}>
                <div className="rules-section-intro">
                  <h3 id={`${id}-allocation`}>Cómo se reparten los huecos</h3>
                  <small>Hasta {s.table.max} casos</small>
                </div>
                <p>{allocationText}</p>
                <figure className="rules-time-distribution" aria-label="Tiempo de los bloques de casos">
                  {totalMinutes > 0 && (
                    <div className="rules-time-bar" aria-hidden="true">
                      {BLOCKS.map((block) => (
                        <span
                          key={block.id}
                          className={`rules-block--${block.id}`}
                          style={{ flex: s.blockMinutes[block.id] }}
                        />
                      ))}
                    </div>
                  )}
                  <figcaption>
                    {BLOCKS.map((block) => (
                      <div key={block.id} className={`rules-time-label rules-block--${block.id}`}>
                        <i aria-hidden="true" />
                        <span>{block.name}</span>
                        <strong>{s.blockMinutes[block.id]} min</strong>
                      </div>
                    ))}
                  </figcaption>
                </figure>
                <p className="rules-footnote">
                  Estos minutos corresponden a los cuatro bloques de casos, no a la duración completa de la
                  reunión.
                </p>
                <div className="rules-facts">
                  <div>
                    <Layers2 size={19} aria-hidden="true" />
                    <div>
                      <h4>{s.table.groupByCase ? 'Un caso, un hueco' : 'Cada alerta, una fila'}</h4>
                      <p>
                        {s.table.groupByCase
                          ? 'Las alertas del mismo ticket, junta u objeto se agrupan. Puedes ver las señales relacionadas al abrir el caso.'
                          : 'Las alertas se muestran por separado, aunque varias correspondan al mismo caso.'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <Zap size={19} aria-hidden="true" />
                    <div>
                      <h4>La etiqueta «Prioridad máx.»</h4>
                      <p>
                        Destaca los casos de comunidades A/B que están en rojo o tienen una junta{' '}
                        {s.meetingSoon.days === 0 ? 'hoy' : `en ${s.meetingSoon.days} días o menos`}.
                      </p>
                    </div>
                  </div>
                  <div>
                    <UsersRound size={19} aria-hidden="true" />
                    <div>
                      <h4>Lo que prepara el tándem</h4>
                      <p>
                        Puede preparar hasta {MAX_SHEETS_PER_TANDEM} casos con su hoja. La etiqueta «Tándem»
                        los identifica; su entrada en la mesa sigue estas mismas reglas.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              <div className="rules-details-group">
                <details className="rules-details">
                  <summary>
                    <SlidersHorizontal size={17} aria-hidden="true" />
                    <span>
                      Cómo se suman los puntos<small>Pesos actuales y un ejemplo</small>
                    </span>
                    <ChevronDown size={16} aria-hidden="true" />
                  </summary>
                  <PointsDetails rules={rules} rule={example} scoring={s} />
                </details>
                <details className="rules-details">
                  <summary>
                    <CalendarDays size={17} aria-hidden="true" />
                    <span>
                      Límites de cada tipo de alerta<small>Cuándo pasa a ámbar o a rojo</small>
                    </span>
                    <ChevronDown size={16} aria-hidden="true" />
                  </summary>
                  <div className="rules-disclosure-content rules-catalog">
                    <p>
                      «Sin datos» significa que esa fuente aún no aporta información. No significa que no haya
                      incidencias.
                    </p>
                    {BLOCKS.map((block) => (
                      <section key={block.id} aria-label={block.name}>
                        <h4>
                          {block.name} <small>{s.blockMinutes[block.id]} min</small>
                        </h4>
                        <ul>
                          {systemRules
                            .filter((rule) => rule.block === block.id)
                            .map((rule) => (
                              <li key={rule.id}>
                                <div className="rules-catalog-title">
                                  <strong>{rule.name}</strong>
                                  <small className={rule.enabled ? 'rules-rule-active' : ''}>
                                    {rule.enabled ? 'Activa' : 'Sin datos'}
                                  </small>
                                </div>
                                <p>{rule.measure}</p>
                                <div className="rules-catalog-thresholds">
                                  <span className="rules-tone--warn">
                                    Revisar: {thresholdText(rule, rule.amber)}
                                  </span>
                                  <span className="rules-tone--bad">
                                    Atender: {thresholdText(rule, rule.red)}
                                  </span>
                                </div>
                                {rule.fallback && (
                                  <>
                                    <p>{rule.fallback.measure}</p>
                                    <div className="rules-catalog-thresholds">
                                      <span className="rules-tone--warn">
                                        Revisar: {thresholdText(rule, rule.fallback.amber)}
                                      </span>
                                      <span className="rules-tone--bad">
                                        Atender: {thresholdText(rule, rule.fallback.red)}
                                      </span>
                                    </div>
                                  </>
                                )}
                                <small>
                                  {rule.id} · {rule.sourceLabel}
                                </small>
                              </li>
                            ))}
                        </ul>
                      </section>
                    ))}
                  </div>
                </details>
              </div>
              <p className="rules-snapshot-note">
                <Clock3 size={14} aria-hidden="true" /> Las alertas corresponden a la foto de datos que estás
                viendo.
              </p>
            </Sheet.Body>
            <Sheet.Footer className="rules-footer">
              {onOpenAdmin && (
                <Button variant="ghost" onPress={onOpenAdmin}>
                  <ShieldCheck size={16} aria-hidden="true" /> Ajustar en Administración
                </Button>
              )}
              <Button className="rules-done" onPress={onClose}>
                <Check size={16} aria-hidden="true" /> Entendido
              </Button>
            </Sheet.Footer>
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}
